/**
 * Configuration centrale du système d'impression d'étiquettes.
 *
 * - LABEL_FORMATS : dimensions physiques (mm) des formats supportés.
 * - LABEL_TYPES   : pour chaque type d'étiquette (product / delivery / client),
 *                   la liste des champs affichables + le modèle par défaut.
 *
 * Les "champs" décrivent comment extraire une valeur depuis l'objet métier (`accessor`)
 * et comment l'afficher (`label`, `kind`). `LabelContent` consomme cette config.
 */

// Conversion millimètres → pixels pour l'aperçu écran (96 dpi ≈ 3.78 px/mm).
export const MM_TO_PX = 96 / 25.4;

export const LABEL_FORMATS = [
    {id: "50x25", label: "50 × 25 mm", width: 50, height: 25},
    {id: "50x30", label: "50 × 30 mm", width: 50, height: 30},
    {id: "58x40", label: "58 × 40 mm", width: 58, height: 40},
    {id: "40x30", label: "40 × 30 mm", width: 40, height: 30},
    {id: "100x150", label: "100 × 150 mm", width: 100, height: 150},
];

export const getFormat = (id) =>
    LABEL_FORMATS.find((f) => f.id === id) || LABEL_FORMATS[0];

export const isValidFormat = (id) => LABEL_FORMATS.some((f) => f.id === id);

/**
 * Bornes appliquées à la saisie des quantités d'étiquettes.
 * - maxPerItem : plafond par produit (garde-fou saisie).
 * - maxTotal   : plafond global d'étiquettes générées en une impression
 *               (protège le navigateur des très grands lots).
 */
export const LABEL_LIMITS = {
    minPerItem: 1,
    maxPerItem: 999,
    maxTotal: 2000,
};

/**
 * Champs disponibles par type d'étiquette.
 * `kind`:
 *   - "title"   : titre principal (gras)
 *   - "text"    : ligne de texte simple
 *   - "price"   : valeur monétaire (DH)
 *   - "barcode" : rendu en code-barres
 *   - "qr"      : rendu en QR code
 * `accessor`: (item) => valeur brute (string/number) ou null.
 */
export const LABEL_TYPES = {
    product: {
        title: "Étiquette produit",
        defaultFormat: "50x25",
        fields: [
            {id: "name", label: "Nom du produit", kind: "title", accessor: (p) => p.name},
            {id: "category", label: "Catégorie", kind: "text", accessor: (p) => p.category?.name},
            {id: "brand", label: "Marque", kind: "text", accessor: (p) => p.brand?.name},
            {id: "reference", label: "Référence", kind: "text", accessor: (p) => p.reference || p.id},
            {id: "price", label: "Prix", kind: "price", accessor: (p) => p.price},
            {id: "barcode", label: "Code-barres", kind: "barcode", accessor: (p) => p.barcode},
            {id: "qr", label: "QR code", kind: "qr", accessor: (p) => p.barcode || p.reference || p.id},
        ],
        defaultSelected: ["name", "price", "barcode"],
    },
    delivery: {
        title: "Étiquette de livraison",
        defaultFormat: "100x150",
        fields: [
            {id: "orderNumber", label: "N° de commande", kind: "title", accessor: (o) => o.orderNumber},
            {id: "provider", label: "Fournisseur / Client", kind: "text", accessor: (o) => o.providerName},
            {id: "date", label: "Date", kind: "text", accessor: (o) => o.orderDate ? new Date(o.orderDate).toLocaleDateString("fr-FR") : null},
            {id: "total", label: "Total", kind: "price", accessor: (o) => o.totalAmount},
            {id: "barcode", label: "Code-barres de suivi", kind: "barcode", accessor: (o) => o.orderNumber},
            {id: "qr", label: "QR code de suivi", kind: "qr", accessor: (o) => o.orderNumber},
        ],
        defaultSelected: ["orderNumber", "provider", "qr"],
    },
    client: {
        title: "Étiquette client",
        defaultFormat: "58x40",
        fields: [
            {id: "name", label: "Nom du client", kind: "title", accessor: (c) => [c.firstName, c.lastName].filter(Boolean).join(" ") || c.name},
            {id: "phone", label: "Téléphone", kind: "text", accessor: (c) => c.phone},
            {id: "address", label: "Adresse", kind: "text", accessor: (c) => [c.adresse, c.city].filter(Boolean).join(", ")},
            {id: "clientId", label: "Identifiant client", kind: "text", accessor: (c) => c.id},
            {id: "barcode", label: "Code-barres", kind: "barcode", accessor: (c) => c.id},
            {id: "qr", label: "QR code", kind: "qr", accessor: (c) => c.id || c.phone},
        ],
        defaultSelected: ["name", "phone", "qr"],
    },
};

export const getTypeConfig = (type) => LABEL_TYPES[type] || LABEL_TYPES.product;
